import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Pause, Play, X } from "lucide-react";
import { toast } from "sonner";

const AZIONE_MSG = {
  avviato: "È stato avviato automaticamente il funnel",
  cambiato: "Il funnel è passato automaticamente a",
  messo_in_pausa: "Il funnel è stato messo in pausa automaticamente:",
  nessuna: "Funnel collegato:",
};

const itDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");

export default function FunnelActivityPrompt() {
  const [open, setOpen] = useState(false);
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const h = (e) => { setP(e.detail); setOpen(true); };
    window.addEventListener("prospect-funnel-prompt", h);
    return () => window.removeEventListener("prospect-funnel-prompt", h);
  }, []);

  if (!p) return null;

  const done = (msg) => {
    toast.success(msg);
    setOpen(false);
    window.dispatchEvent(new CustomEvent("prospect-funnel-updated", { detail: { prospect_id: p.prospect_id } }));
  };
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); done(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const mantieni = () => {
    if (p.stato === "in_pausa") return act(() => api.post(`/prospects/${p.prospect_id}/funnel/riprendi`), "Funnel mantenuto attivo");
    setOpen(false);
  };
  const pausa = () => act(() => api.post(`/prospects/${p.prospect_id}/funnel/pausa`, { motivo: "manuale" }), "Funnel messo in pausa");
  const termina = () => act(() => api.post(`/prospects/${p.prospect_id}/funnel/termina`, { motivo: "terminato manualmente" }), "Funnel terminato");

  const label = p.tipo ? `${p.tipo} · ${p.tipo_nome || ""}` : "—";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="funnel-activity-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-emerald-600" /> Funnel del prospect</DialogTitle>
          <DialogDescription>
            {p.prospect_name ? <span className="font-semibold text-foreground">{p.prospect_name}</span> : null}
            <span className="mt-2 block">{AZIONE_MSG[p.azione] || AZIONE_MSG.nessuna} <b>{label}</b>{p.stato === "in_pausa" ? " — ora in pausa." : "."}</span>
            <span className="mt-2 block text-xs">Vuoi mantenere attivo il funnel per questo prospect?</span>
          </DialogDescription>
        </DialogHeader>
        {p.storico && p.storico.length > 0 ? (
          <div className="max-h-40 space-y-1 overflow-auto rounded-md border border-border bg-muted/30 p-2" data-testid="funnel-prompt-storico">
            <div className="mb-1 text-xs font-semibold text-muted-foreground">Email già inviate ({p.storico.length})</div>
            {p.storico.map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate">{s.subject}</span>
                <span className="shrink-0 font-mono text-muted-foreground">{itDate(s.sent_at)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="funnel-prompt-storico-empty">Nessuna email del funnel ancora inviata a questo prospect.</p>
        )}
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" className="text-red-600 hover:text-red-700" disabled={busy} onClick={termina} data-testid="funnel-prompt-termina"><X className="mr-1 h-4 w-4" /> Termina</Button>
          <Button variant="outline" disabled={busy || p.stato === "in_pausa"} onClick={pausa} data-testid="funnel-prompt-pausa"><Pause className="mr-1 h-4 w-4" /> Metti in pausa</Button>
          <Button disabled={busy} onClick={mantieni} data-testid="funnel-prompt-mantieni"><Play className="mr-1 h-4 w-4" /> Mantieni attivo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
